import { useCallback, useEffect, useState } from 'react';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

// Single fixed key holds a { contentId: lastOpenedISO } map so the Browser
// Storage Controls registration stays static. Namespaced per user by StorageManager.
const STORAGE_KEY = keys.AI_SUGGESTED_EDITS_SEEN_SIGNATURE;

type LastOpenedMap = Record<string, string>;

const readMap = (): LastOpenedMap => {
	const stored = confluenceLocalStorageInstance?.getItem(STORAGE_KEY);
	return stored && typeof stored === 'object' && !Array.isArray(stored)
		? (stored as LastOpenedMap)
		: {};
};

const readLastOpened = (contentId: string): string | null =>
	contentId ? (readMap()[contentId] ?? null) : null;

export interface UseUnseenSuggestionsResult {
	// True when a suggestion was created after the user last opened this page's suggestions.
	hasUnseenSuggestions: boolean;
	// Records "now" as the last-opened time for this page.
	markSuggestionsSeen: () => void;
}

// Tracks, per browser and per content id, whether any AI suggestion was created
// since the user last viewed this page's suggestions (local-only, role-agnostic).
// Comparing creation timestamps against a "last opened" time avoids false
// positives when suggestions are merely re-ordered rather than added.
// With `skip`, it neither reads nor writes storage, has nothing unseen, and marking seen does nothing.
export const useUnseenSuggestions = (
	contentId: string,
	latestSuggestionCreatedAt: string | undefined,
	{ skip = false }: { skip?: boolean } = {},
): UseUnseenSuggestionsResult => {
	// Held in React state (seeded from storage) so marking seen re-renders and
	// clears the dot; a bare storage read during render would not.
	const [lastOpened, setLastOpened] = useState<string | null>(() =>
		skip ? null : readLastOpened(contentId),
	);

	// Re-seed on contentId change: on a live-doc transition the component stays
	// mounted while contentId changes, so the lazy initializer would not re-run.
	useEffect(() => {
		if (!skip) {
			setLastOpened(readLastOpened(contentId));
		}
	}, [contentId, skip]);

	// Unseen when there is a suggestion and either the page was never opened or
	// the newest suggestion was created after it was last opened.
	const hasUnseenSuggestions =
		!skip &&
		!!latestSuggestionCreatedAt &&
		(lastOpened === null || latestSuggestionCreatedAt > lastOpened);

	const markSuggestionsSeen = useCallback(() => {
		if (skip || !contentId) {
			return;
		}
		const now = new Date().toISOString();
		confluenceLocalStorageInstance?.setItem(STORAGE_KEY, { ...readMap(), [contentId]: now });
		setLastOpened(now);
	}, [contentId, skip]);

	return { hasUnseenSuggestions, markSuggestionsSeen };
};
